/**
 * useSubscriptionStatus — Returns the current user's subscription and derived UI state.
 *
 * The subscription record is fetched once and kept in the react-query cache.
 * Use `refetch()` after a successful checkout to update the UI.
 *
 * Returns:
 *   subscription     — raw subscription model (null if none)
 *   displayState     — 'active' | 'past_due' | 'canceled_active' | 'expired' | 'none'
 *   isPro            — true if status is active/trialing/past_due
 *   isLoading
 *   error
 *   refetch
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { subscriptionRepository } from '../repositories/supabaseSubscriptionRepository';
import type { SubscriptionModel } from '../repositories/types';

/**
 * Display states used across the UI.
 *
 * `suspended` (Phase 8 / SOS-335) is a sOSSiety-only state: Stripe still
 * reports `past_due` but our D+2 cron has revoked benefits. The user sees a
 * stronger "suspended" banner and feature gates deny access.
 */
export type SubscriptionDisplayState =
  | 'active'
  | 'past_due'
  | 'suspended'
  | 'canceled_active'
  | 'expired'
  | 'none';

const PRO_STATUSES = new Set(['active', 'trialing', 'past_due']);

function deriveDisplayState(
  sub: SubscriptionModel | null | undefined
): SubscriptionDisplayState {
  if (!sub) return 'none';

  // Phase 8: suspension wins over past_due — UI must show the harsher message.
  if (sub.status === 'past_due' && sub.isSuspended) return 'suspended';
  if (sub.status === 'past_due') return 'past_due';

  if (sub.status === 'canceled') {
    if (sub.currentPeriodEnd && sub.currentPeriodEnd > new Date()) return 'canceled_active';
    return 'expired';
  }

  if ((sub.status === 'active' || sub.status === 'trialing') && sub.cancelAtPeriodEnd) {
    return 'canceled_active';
  }

  if (sub.status === 'active' || sub.status === 'trialing') return 'active';

  return 'expired';
}

export function useSubscriptionStatus() {
  const { user } = useAuth();

  const {
    data: subscription,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: () => subscriptionRepository.fetchMySubscription(user!.id),
    enabled: !!user,
    staleTime: 5 * 60 * 1_000,    // 5 min — webhook updates invalidate via refetch()
    gcTime:    10 * 60 * 1_000,
  });

  const displayState = deriveDisplayState(subscription);
  // Suspended users are NOT Pro for UI purposes — feature gates also deny.
  const isPro = subscription
    ? PRO_STATUSES.has(subscription.status) && !subscription.isSuspended
    : false;

  return {
    subscription: subscription ?? null,
    displayState,
    isPro,
    isLoading,
    error: error instanceof Error ? error.message : null,
    refetch,
  };
}
